import type { ProductWithHyperguestHotel } from '@travel-planner/shared/schemas';
import { Hotel, Package, X } from 'lucide-react';
import { pickCss } from '@/shared/lib/css-utils';
import {
  Button,
  EmptyState,
  Pagination,
  PaginationButton,
  Spinner,
  Text,
  Tooltip,
} from '@/shared/ui';
import { hotelRowDetails, productRowDetails } from '../model/row-details';
import { sortPendingFirst } from '../model/sort-pending-first';
import { formatLocation, resolveTitle } from '../model/title';
import css from './hyperguest-mapper.module.scss';
import { detailTooltipBubble, MatchDetailTooltip } from './row-detail-tooltip';

const rowStyles = pickCss(css, css.mapperRow);

type Props = {
  rows: ProductWithHyperguestHotel[];
  totalPages: number;
  currentPage: number;
  loading: boolean;
  pendingIds: Set<string>;
  onUnmatch: (productId: string) => void;
  onPageChange: (page: number) => void;
};

export const MatchesTab = ({
  rows,
  totalPages,
  currentPage,
  loading,
  pendingIds,
  onUnmatch,
  onPageChange,
}: Props) => (
  <div className={css.tablePane}>
    {loading ? (
      <Spinner />
    ) : rows.length === 0 ? (
      <EmptyState
        title="No confirmed matches yet"
        description="Confirm an AI suggestion or pick a product + HG hotel and Save."
      />
    ) : (
      <>
        <ul className={css.cardList}>
          {sortPendingFirst(rows, pendingIds).map((pair) => (
            <li
              className={rowStyles({
                isPending: pendingIds.has(pair.productHotel.id),
              })}
              key={pair.productHotel.id}
            >
              <Tooltip
                className={css.rowMain}
                bubbleClassName={detailTooltipBubble}
                position="bottom"
                content={
                  <MatchDetailTooltip
                    product={productRowDetails(pair.productHotel)}
                    hotel={hotelRowDetails(pair.hyperguestHotel)}
                  />
                }
              >
                <div className={css.metaLine}>
                  <Package className={css.metaIcon} />
                  <Text weight="semibold" size="sm">
                    {resolveTitle(pair.productHotel.title)}
                  </Text>
                </div>
                <div className={css.metaLine}>
                  <Hotel className={css.metaIcon} />
                  <Text weight="semibold" size="sm">
                    {pair.hyperguestHotel.title}
                  </Text>
                </div>
                <Text variant="muted" size="xs">
                  {formatLocation(
                    pair.hyperguestHotel.city,
                    pair.hyperguestHotel.country
                  )}
                </Text>
              </Tooltip>
              <div className={css.rowActions}>
                <Button
                  size="sm"
                  variant="outline"
                  icon={<X style={{ height: 14, width: 14 }} />}
                  aria-label="Unmatch"
                  title="Unmatch"
                  onClick={() => onUnmatch(pair.productHotel.id)}
                />
              </div>
            </li>
          ))}
        </ul>
        {totalPages > 1 && (
          <Pagination>
            <PaginationButton
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              Prev
            </PaginationButton>
            <Text size="sm" variant="muted">
              Page {currentPage} of {totalPages}
            </Text>
            <PaginationButton
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              Next
            </PaginationButton>
          </Pagination>
        )}
      </>
    )}
  </div>
);
